import { useState, useEffect, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Typography, CardMedia, Box, Paper } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardSharpIcon from '@mui/icons-material/ArrowForwardSharp';
import Chip from '@mui/material/Chip';
import { useMediaQuery, useTheme } from '@mui/system';
import Player from '@vimeo/player';
import { useTranslation } from 'react-i18next';

// Local imports
import { fetchVimeoData } from "../../api/videos-service";
import { ERROR_MESSAGES, ACTIVITY_TYPES } from "../../api/constants";
import { api } from "../../api";
import { useActivitiesCompletion } from '../ActivitiesCompletionContext'
import Loading from "../Loading";
import CustomLink from "../CustomLink";
import './Video.css';

// Environment variables
const SURVEY_REQUIRED_VIDEO_ID = process.env.REACT_APP_SURVEY_REQUIRED_VIDEO_ID ? Number(process.env.REACT_APP_SURVEY_REQUIRED_VIDEO_ID) : 2;
const MIN_SECONDS_TO_WATCH_VIDEO = process.env.REACT_APP_MIN_SECONDS_TO_WATCH_VIDEO ? Number(process.env.REACT_APP_MIN_SECONDS_TO_WATCH_VIDEO) : 5;
const VIDEO_COMPLETION_THRESHOLD = process.env.REACT_APP_VIDEO_COMPLETION_THRESHOLD ? Number(process.env.REACT_APP_VIDEO_COMPLETION_THRESHOLD) : 0.95;

const Video = () => {
  const { id } = useParams();
  const [ vimeoURL, setVimeoURL ] = useState("");
  const [ videoData, setVideoData ] = useState({});
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState('');
  const [ isCompleted, setIsCompleted ] = useState(false);
  const [ nextVideo, setNextVideo ] = useState(null);
  const [ previousVideo, setPreviousVideo ] = useState(null);

  const { t, i18n } = useTranslation();
  const currentLng = i18n.language;
  const iframeRef = useRef(null);
  const completedRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setReloadActivities } = useActivitiesCompletion();
  
  if (!id || !/^\d+$/.test(id)) {
    setError("Invalid video ID");
  }

  useEffect(() => {
    // Fetch vimeo URL
    const fetchData = async() => {
      try {
        const data = await api.get(`/videos/${id}`);
        setVimeoURL(data.vimeo_url);
      } catch(err) {
        if (err.message === ERROR_MESSAGES.NOT_FOUND) {
          setError("Video with ID " + id + " does not exist.");
        } else if (err.message === ERROR_MESSAGES.UNAUTHORIZED_ACCESS) {
          setError("You must be logged in to view this video.");
        } else {
          setError(err.message);
        }
      }
    }
    fetchData();

    // Check if user has access to the video
    const checkUserAccess = async() => {
      try {
        const user = await api.get('/auth/profile');
        if (!user) {
          setError("You must be logged in to view this video.");
          return;
        }
        if (!user.videos_in_order || user.videos_in_order.length === 0) {
          setError("You do not have access to any videos.");
          return;
        }
        // Check if current video is assigned to the user
        if (user.videos_in_order.findIndex((video) => video.id === Number(id)) === -1) {
          setError("You do not have access to this video.");
          return;
        }
        // If user has one video assigned, allow access to it
        if (user.videos_in_order.length === 1) {
          return;
        }

        user.videos_in_order.forEach((video) => {
          if (video.order === 1 && video.id === Number(id)) {
            return; // Always allow access to the first video
          }
          // Block access to the current video if the previous one is not completed
          if (video.id === Number(id) && (user.videos_in_order.findIndex((v) => v.order === video.order - 1) !== -1 && !user.videos_in_order.find((v) => v.order === video.order - 1).completed)) {
            setError("You must complete the previous video before accessing this one.");
            return;
          }
        })

        // Check if user has completed the current video
        if (user.videos_in_order.find((v) => v.id === Number(id))?.completed) {
          setIsCompleted(true);
          completedRef.current = true;
        } else {
          setIsCompleted(false);
          completedRef.current = false;
        }

        // Check if user has the next assigned video
        const videoOrder = user.videos_in_order.find((v) => v.id === Number(id))?.order;
        if (user.videos_in_order.findIndex((v) => v.order === videoOrder + 1) !== -1) {
          const nextVideoId = user.videos_in_order.find((v) => v.order === videoOrder + 1).id;
          setNextVideo(`/videos/${nextVideoId}`);
        } else {
          setNextVideo(null);
        }

        // Set previous video link
        if (videoOrder > 1) {
          const previousVideoId = user.videos_in_order.find((v) => v.order === videoOrder - 1).id;
          setPreviousVideo(`/videos/${previousVideoId}`);
        } else {
          setPreviousVideo(null);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError(err.message);
      }
    }
    checkUserAccess();
  }, [id]);

  useEffect(() => {
    if (!vimeoURL) {
      return;
    }
    setLoading(true);
    // Fetch video data from Vimeo
    const fetchVideoData = async() => {
      try {
        const data = await fetchVimeoData(vimeoURL);
        setVideoData(data);
      } catch(err) {
        if (err.message === ERROR_MESSAGES.NOT_FOUND) {
          setError("Error fetching video from Vimeo. Please check back later.");
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchVideoData();
  }, [vimeoURL]);

  useEffect(() => { 
    if (!iframeRef.current) {
      return;
    }
    const iframe = iframeRef.current;
    const player = new Player(iframe, { url: vimeoURL });

    // Initialize play time for tracking when user started watching the video
    let playTime = 0;

    // Track the latest playhead via `timeupdate` so we can log pre-seek watch time.
    let lastKnownTime = 0;

    // Vimeo fires both `pause` and `ended` when a video plays to completion;
    // this flag ensures one watch-segment log per play→stop cycle.
    let segmentLogged = false;
    const handlePlay = (e) => {
      playTime = e.seconds;
      lastKnownTime = e.seconds;
      segmentLogged = false;
    }

    // timeupdate fires ~4×/sec, so a normal delta is ~0.25s (up to ~0.5s at 2× playback).
    // A larger jump means the playhead just seeked — ignore that update so `lastKnownTime`
    // stays at the pre-seek position until `handleSeeked` re-syncs it.
    const MAX_NORMAL_TIME_DELTA = 1.0;
    const handleTimeUpdate = (e) => {
      if (Math.abs(e.seconds - lastKnownTime) > MAX_NORMAL_TIME_DELTA) {
        return;
      }
      lastKnownTime = e.seconds;
    }

    // Log how much of the video was watched in this play/pause segment.
    const logWatchedSegment = (currentSeconds) => {
      if (segmentLogged) {
        return;
      }
      const durationWatched = calculateDurationWatched(playTime, currentSeconds);
      if (durationWatched < MIN_SECONDS_TO_WATCH_VIDEO) {
        return;
      }
      segmentLogged = true;
      createActivityLog({
        activityType: ACTIVITY_TYPES.USER_WATCHED_VIDEO,
        durationWatched: durationWatched,
        videoId: Number(id),
        videoName: videoData.title,
      });
    }

    // Marks the video completed. Writes the activity log and the user-table
    // flag together so the UI only flips to "completed" if both succeed.
    const handleCompletion = async (startedFromBeginning) => {
      if (completedRef.current) {
        return;
      }
      completedRef.current = true;
      try {
        await Promise.all([
          api.post('/activity/create', {
            activityType: ACTIVITY_TYPES.USER_COMPLETED_VIDEO,
            durationWatched: null,
            videoId: Number(id),
            videoName: videoData.title,
            startedFromBeginning,
          }),
          api.put(`/videos/${id}/completed`),
        ]);
        setIsCompleted(true);
        // The context refetches assigned-video state from this flag and runs the
        // single completion-modal trigger when all activities are done.
        setReloadActivities(true);
      } catch (err) {
        console.error('Error marking video as completed:', err);
        // Release the guard so the next pause/ended event can retry.
        completedRef.current = false;
      }
    }

    const handlePause = (e) => {
      logWatchedSegment(e.seconds);
      if (e.percent >= VIDEO_COMPLETION_THRESHOLD) {
        handleCompletion(playTime === 0);
      }
    }

    const handleEnded = (e) => {
      logWatchedSegment(e.seconds);
      handleCompletion(playTime === 0);
    }

    const handleSeeked = (e) => {
      // `lastKnownTime` still holds the pre-seek position because handleTimeUpdate
      // rejected the jump. Log the watch segment up to that point before resetting.
      logWatchedSegment(lastKnownTime);
      playTime = e.seconds;
      lastKnownTime = e.seconds;
      // Seeking starts a new segment, so allow the next pause/end to log again.
      segmentLogged = false;
    }

    // Add subtitles based on the current language
    player.enableTextTrack(currentLng).catch((err) => {
      console.error('Error enabling text track:', err);
    });

    player.on('play', handlePlay);
    player.on('pause', handlePause);
    player.on('ended', handleEnded);
    player.on('seeked', handleSeeked);
    player.on('timeupdate', handleTimeUpdate);

    return () => {
      player.off('play', handlePlay);
      player.off('pause', handlePause);
      player.off('ended', handleEnded);
      player.off('seeked', handleSeeked);
      player.off('timeupdate', handleTimeUpdate);
      player.destroy();
    }
  }, [vimeoURL, id, loading, currentLng]);

  // Calculate the duration a user watched the video
  const calculateDurationWatched = (playTime, pauseTime) => {
    return Math.round((pauseTime - playTime) * 100) / 100;
  }

  const createActivityLog = async (data) => {
    try {
      await api.post('/activity/create', data);
    } catch (err) {
      console.error('Error creating activity log:', err);
    }
  }
  // Error message
  if (error) {
    throw new Error(error);
  }

  // Loading icon
  if (loading) {
    return <Loading />;
  }

  return (
    <div className="main">
      <Box sx={{ mt: 2, maxWidth: '1500px', width: { xs: '100%', sm: '90%', md: '80%' }, margin: 'auto', mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Link 
            to={previousVideo || "/videos"} 
            style={{ fontSize: {xs: '12px', md: '20px'} }}>
              <ArrowBackIcon 
                sx={{ 
                  mr: 1, 
                  verticalAlign: 'middle', 
                  fontSize: { 
                    xs: 'medium', 
                    sm: 'large'
                  } 
                }} 
              />
              {previousVideo ? t("videos.previous") : t("videos.go_back")}
          </Link>
          {nextVideo && 
            <Link 
              to={nextVideo} 
              style={{ 
                fontSize: {
                  xs: '12px', 
                  md: '20px'
                },
                pointerEvents: !isCompleted ? 'none' : 'auto', 
                opacity: !isCompleted ? 0.5 : 1,
                color: !isCompleted ? 'grey' : ''
              }}>
              {t("videos.next")}
              <ArrowForwardSharpIcon 
                sx={{ 
                  ml: 1, 
                  verticalAlign: 'middle', 
                  fontSize: { 
                    xs: 'medium', 
                    sm: 'large'
                  } 
                }} 
              />
            </Link>}
        </Box>
        
        <Paper elevation={3} sx={{ padding: { xs: 2, sm: 4 }, mb: 4, borderRadius: '10px', mt: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: '700', mb: 2}}>{t(`videos.video_${id}_title`) || ''}</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>{t(`videos.video_${id}_description`) || ''}</Typography>
          <Typography variant="body2">
            <em>
              <strong>{t("videos.note")}</strong> 
              {t("videos.note_description")}
            </em>
          </Typography>
        </Paper>

        {/* Add the survey link to the video with id 2 */}
        {id == SURVEY_REQUIRED_VIDEO_ID && <CustomLink to="/survey" linkText={t("videos.survey_link")} disabled={!isCompleted} />}

        {/* Add a helper text for mobile users */}
        {isMobile && <Chip 
                        label={t("videos.mobile_note")} 
                        color="warning"
                        sx={{ 
                          display: 'flex', 
                          margin: 'auto', 
                          height: 'auto',
                          width: 'fit-content',
                          padding: 0.5,
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            textAlign: 'center',
                          }
                        }} 
                      />}
      <div
        id="vimeo-player-container"
        key={vimeoURL}
        ref={iframeRef}
        allow="fullscreen"
        style={{
          position: 'relative', 
          paddingBottom: '56.25%', 
          overflow: 'hidden',
          width: '100%',
          marginTop: 4,
        }}
      ></div>
      </Box>
          
    </div>
  )
}

export default Video;